import { FaWhatsapp, FaCheckDouble, FaWifi, FaBatteryFull, FaSignal } from 'react-icons/fa';
import { IoArrowBack, IoVideocam, IoCall } from 'react-icons/io5';
import { BsThreeDotsVertical, BsEmojiSmile, BsMicFill } from 'react-icons/bs';
import { MdAttachFile, MdCameraAlt } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { resolveWhatsappPreviewFields, type WhatsappPreviewFormData } from './resolveWhatsappPreviewFields';

export type TemplateFormData = WhatsappPreviewFormData;

function replaceTemplateParams(text: string | undefined, examples: string[] | undefined, parameterFormat: 'POSITIONAL' | 'NAMED' | undefined) {
  if (!text) return '';

  if (parameterFormat === 'NAMED') {
    let index = 0;

    return text.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, () => {
      const example = examples?.[index];
      index += 1;

      return example || `{{example_${index}}}`;
    });
  }

  return text.replace(/\{\{(\d+)\}\}/g, (_, number) => {
    const example = examples?.[Number(number) - 1];

    return example || `{{${number}}}`;
  });
}

function getCategoryBadge(category?: string) {
  switch (category) {
    case 'MARKETING':
      return 'Marketing';
    case 'UTILITY':
      return 'Utility';
    case 'AUTHENTICATION':
      return 'Authentication';
    default:
      return 'Template';
  }
}

export const TemplatePreview = ({ formData }: { formData: TemplateFormData }) => {
  const { t } = useTranslation('campaign');
  const { t: tcommon } = useTranslation('COMMON');

  const localTime = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());

  const resolved = resolveWhatsappPreviewFields(formData);
  const parameterFormat = resolved.parameterFormat || tcommon('positionalText');

  const headerText = replaceTemplateParams(resolved.header, resolved.headerExamples, resolved.parameterFormat);
  const bodyText = replaceTemplateParams(resolved.body, resolved.bodyExamples, resolved.parameterFormat);
  const footerText = resolved.footer;

  return (
    <aside className='sticky top-6'>
      <div className='mb-3 flex items-center justify-between'>
        <div>
          <h2 className='text-sm font-semibold text-gray-900'>{t('whatsappWorkspace.whatsappPreview')}</h2>
          <p className='mt-1 text-xs text-gray-500'>{t('whatsappWorkspace.whatsappPreviewDescription')}</p>
        </div>

        <span className='rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700'>{getCategoryBadge(resolved.category)}</span>
      </div>

      <div className='mx-auto w-full max-w-[390px] overflow-hidden rounded-[2rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl'>
        <div className='overflow-hidden rounded-[1.45rem] bg-[#fff]'>
          {/* Phone status bar */}
          <div className='flex h-7 items-center justify-between bg-[#fff] px-5 text-[11px] text-white'>
            <span>{localTime}</span>

            <div className='flex items-center gap-1.5'>
              <FaSignal className='text-[10px]' />
              <FaWifi className='text-[11px]' />
              <FaBatteryFull className='text-[12px]' />
            </div>
          </div>

          {/* WhatsApp header */}
          <div className='flex h-14 items-center gap-3 bg-[#075e54] px-3 text-white'>
            <IoArrowBack className='text-xl' />

            <div className='flex h-9 w-9 items-center justify-center rounded-full bg-[#128c7e]'>
              <FaWhatsapp className='text-xl text-[#fff]' />
            </div>

            <div className='min-w-0 flex-1 text-[#fff]'>
              <div className='truncate text-sm font-semibold'>{resolved.name || 'Business Account'}</div>
              <div className='text-[11px] text-white/80'>online</div>
            </div>

            <div className='flex items-center gap-4 text-xl'>
              <IoVideocam />
              <IoCall />
              <BsThreeDotsVertical />
            </div>
          </div>

          {/* Chat wallpaper */}
          <div className='relative min-h-[520px] bg-[#efe7dd] px-3 py-4'>
            {/* Subtle WhatsApp-like doodle overlay */}
            <div className='pointer-events-none absolute inset-0 opacity-[0.08]'>
              <div className='h-full w-full bg-[radial-gradient(circle_at_20%_20%,#111_1px,transparent_1px),radial-gradient(circle_at_80%_30%,#111_1px,transparent_1px),radial-gradient(circle_at_40%_70%,#111_1px,transparent_1px)] bg-[length:38px_38px]' />
            </div>

            <div className='relative space-y-3'>
              {/* Encryption banner */}
              <div className='mx-auto max-w-[290px] rounded-lg bg-[#fff3c4] px-3 py-2 text-center text-[11px] leading-4 text-[#5f4b00] shadow-sm'>
                {t('whatsappTemplates.encryptedInfo')}
              </div>

              {/* Date chip */}
              <div className='mx-auto w-fit rounded-lg bg-white/80 px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm'>{tcommon('todayText')}</div>

              {/* Message bubble */}
              <div className='flex justify-end'>
                <div className='relative max-w-[82%] overflow-hidden rounded-lg rounded-tr-none bg-[#dcf8c6] shadow-sm'>
                  {/* Bubble corner */}
                  <div className='absolute -right-2 top-0 h-0 w-0 border-b-[10px] border-l-[10px] border-b-transparent border-l-[#dcf8c6]' />

                  {resolved.headerImageUrl ? (
                    <img src={resolved.headerImageUrl} alt='' className='block w-full object-cover' />
                  ) : null}

                  <div className='px-3 py-2'>
                    {!resolved.headerImageUrl && headerText ? (
                      <div className='mb-1.5 text-sm font-semibold leading-5 text-gray-900'>{headerText}</div>
                    ) : null}

                    <div className='whitespace-pre-wrap text-[13px] leading-5 text-gray-900'>
                      {bodyText || <span className='text-gray-500'>{t('whatsappTemplates.messagePreviewPlaceholder')}</span>}
                    </div>

                    {footerText ? <div className='mt-2 border-t border-black/5 pt-1.5 text-[11px] leading-4 text-gray-500'>{footerText}</div> : null}

                    <div className='mt-1 flex items-center justify-end gap-1 text-[10px] text-gray-500'>
                      <span>{localTime}</span>
                      <FaCheckDouble className='text-[11px] text-[#34b7f1]' />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chat input area */}
          <div className='flex items-center gap-2 bg-[#0b141a] px-2 py-2'>
            <div className='flex h-10 flex-1 items-center gap-2 rounded-full bg-white px-3 text-gray-500'>
              <BsEmojiSmile className='text-xl' />

              <div className='flex-1 text-sm text-gray-400'>Message</div>

              <MdAttachFile className='text-xl' />
              <MdCameraAlt className='text-xl' />
            </div>

            <button type='button' className='flex h-10 w-10 items-center justify-center rounded-full bg-[#25d366] text-white'>
              <BsMicFill className='text-lg' />
            </button>
          </div>
        </div>
      </div>

      {/* Template meta */}
      <div className='mt-4 grid grid-cols-2 gap-2 text-xs'>
        <div className='rounded-xl border border-gray-200 bg-white p-3'>
          <div className='text-gray-400'>{tcommon('languageText')}</div>
          <div className='mt-1 font-medium text-gray-800'>{resolved.language ? resolved.language.toUpperCase() : tcommon('notSelectedText')}</div>
        </div>

        <div className='rounded-xl border border-gray-200 bg-white p-3'>
          <div className='text-gray-400'>{tcommon('formatText')}</div>
          <div className='mt-1 font-medium text-gray-800'>{parameterFormat}</div>
        </div>
      </div>
    </aside>
  );
};
